import type { IndustryPage } from "@/lib/data";

export const roofers: IndustryPage = {
  slug: "roofers",
  seoTitle:
    "Roofing Website Design UK | Fixed £1,000, Live in 7 Days",
  seoDescription:
    "Roofing website design for UK roofers: storm callout pages, NFRC badges you hold, photo quote forms, local SEO. Fixed £1,000, live in 7 days. Book a call.",
  serviceName: "Website design for roofers",
  tldr:
    "ConstructionWebDesign.uk offers roofing website design for UK roofers and roofing companies: a mobile-first site with roof-type service pages, storm and emergency callout pages, a photo quote form and the accreditations you hold. The price is £1,000, one-off and fixed, with no subscription. Your website goes live in 7 days from the discovery call, and you own it.",
  pricingFromGBP: 1000,
  hero: {
    eyebrow: "Roofing website design · UK",
    headline: "Website design for roofers, built for storm callouts and quotes.",
    highlight: "storm callouts and quotes",
    sub: "Roofing websites with a page for each roof type, storm and emergency callout pages, a gallery of finished roofs, a photo quote form and the NFRC or CompetentRoofer badges you hold.",
    secondaryCta: { label: "See roofer demos", href: "#demos" },
  },
  slides: [
    {
      bg: "https://constructionwebdesign.cdn.modulify.website/images/industry-hero/Roofers.jpg",
      alt: "Roofing contractor laying natural slate on a pitched roof for a UK home re-roof",
      headlineTop: "Win every roof job",
      headlineBottom: "in your patch.",
      preview: "https://constructionwebdesign.cdn.modulify.website/images/demo-fullheight/ridgemark.jpg",
    },
  ],
  demoCategory: "Roofers",
  demosHeading: "Roofing website design demo templates.",
  demosSub:
    "Demo templates for roofing companies, not client work. Open one to see the layout, page structure and copy style for re-roofs, repairs and flat roofs.",
  value: {
    eyebrow: "Why it matters",
    heading: "Why does a roofer need a website?",
    intro:
      "A roofer needs a website because homeowners and property managers look a roofing company up online before they ring. A roofing company website shows your finished roofs, your accreditations and your reviews, and takes enquiries while you are up a ladder.",
    points: [
      {
        title: "Show the roofs you have finished.",
        body: "Photos of real re-roofs, flat roof renewals and lead work tell a homeowner more than any sales line. A roofing website sorts that work by roof type and town, so a visitor can see a job like theirs before they pick up the phone.",
        icon: "House",
      },
      {
        title: "Be easy to reach when a roof leaks.",
        body: "Storm damage and leaks can't wait. A one-tap call button and a sticky number bar on mobile let a homeowner with water coming through the ceiling ring you straight away, and a dedicated emergency page explains what you cover and where.",
        icon: "Phone",
      },
      {
        title: "Built for \"roofer near me\" searches.",
        body: "On-page local SEO tells Google which towns and postcodes you cover. Each roof-type page, and each finished job that names its town, gives a homeowner searching for a local roofer another route to your site.",
        icon: "Pin",
      },
      {
        title: "Show the accreditations you hold.",
        body: "NFRC (National Federation of Roofing Contractors) membership, CompetentRoofer registration, TrustMark and your insurance cover answer a homeowner's first question: is this roofer safe to trust with my roof? The site shows the ones you hold, linked to the official registers, and leaves off any you don't.",
        icon: "Shield",
      },
    ],
  },
  features: {
    eyebrow: "What's in the site",
    heading: "What does website design for roofers include?",
    intro:
      "Website design for roofers from ConstructionWebDesign.uk includes a page for each roof type, storm and emergency callout pages, a gallery of finished roofs, a photo quote form and the accreditations you hold. Every one of these websites for roofers is mobile-first, with on-page local SEO for the towns you cover.",
    items: [
      {
        title: "Emergency and storm callout pages with one-tap call.",
        body: "Your phone number sits at the top of the first screen, not under a slideshow, with a one-tap call button that stays visible on mobile as visitors scroll. Separate storm damage and emergency repair pages explain the urgent work you take on, such as leaks, slipped tiles and temporary covers, and the areas you can reach.",
      },
      {
        title: "A page for every roof type.",
        body: "Pitched roofs, slate, tile, flat roofs in EPDM (rubber membrane) and GRP (fibreglass), lead work, chimneys, guttering, and fascias and soffits each get their own page. Each page is written around what a homeowner types into Google, such as \"EPDM flat roof replacement\" or \"slate roof repair\" plus your town.",
      },
      {
        title: "A gallery of finished roofs, with before and after photos.",
        body: "Each finished job gets its own page with before and after photos, the roof type, the materials used and the town. A homeowner can see a slate re-roof or a GRP flat roof you have actually done, and every job page gives Google another description of the work you do and where.",
      },
      {
        title: "A photo quote form.",
        body: "Homeowners can upload a photo of the problem with their postcode and a short description. You see the slipped slates, cracked flashing or split flat roof before you call back, so you can give a rough idea of price or decide whether a site visit is worth the trip.",
      },
      {
        title: "Price guides for common roofing jobs.",
        body: "If you want to publish them, the site can show your own typical price ranges for jobs such as a slate re-roof, a flat roof renewal or a chimney repair. The figures and wording are yours, and nothing goes live that you have not approved.",
      },
      {
        title: "NFRC, CompetentRoofer and TrustMark badges, if you hold them.",
        body: "NFRC membership, CompetentRoofer registration (the competent person scheme that lets roofers self-certify re-roofing work under Building Regulations) and TrustMark sit near the top of the page, with registration numbers linked to the official register. Where a job had LABC building control sign-off, its project page can say so. If you don't hold a scheme, its badge stays off the site.",
      },
      {
        title: "Mobile-first pages with on-page local SEO.",
        body: "Every page is designed for a phone first, so a homeowner checking you out from the garden or the loft hatch can read it and call in one tap. Page titles, headings and copy name your roof types and the towns and postcodes you cover.",
      },
    ],
  },
  faqs: [
    {
      q: "How much does a roofing website cost in the UK?",
      a: "A roofing website from ConstructionWebDesign.uk costs £1,000, a one-off fixed price with no subscription. The Website + Brand package costs £1,399 and adds a logo, visual identity and business card mockups. For comparison, typical UK ranges are about £4,000–£5,000 for a freelancer and £5,000–£25,000 for an agency, while Wix or Squarespace cost about £22 a month. You own the finished website.",
    },
    {
      q: "How long does it take to build a roofing website?",
      a: "A roofing website from ConstructionWebDesign.uk goes live in 7 days from the discovery call. The process runs in five steps: discovery call, strategy and sitemap, design, build, then launch and support. Having photos of finished roofs, your accreditation numbers and your list of services ready for the discovery call keeps the week on track.",
    },
    {
      q: "What should a roofing company website include?",
      a: "A roofing company website should include a one-tap call button, a page for each roof type (pitched, slate and tile, EPDM and GRP flat roofs, lead work, chimneys, guttering), storm and emergency callout pages, a gallery of finished roofs, a photo quote form, reviews and the accreditations you hold, such as NFRC or CompetentRoofer. Every page should be easy to use on a phone.",
    },
    {
      q: "Do roofers need a website?",
      a: "Yes, a roofer needs a website, even as a sole trader. Homeowners with a leak or a tired roof check roofers online before they ring, and a website is where you can show finished roofs, NFRC or CompetentRoofer registration if you hold it, reviews and the areas you cover. Without one, a homeowner has little to compare you on except price.",
    },
    {
      q: "How do roofing websites get found on Google?",
      a: "Roofing websites get found on Google through on-page local SEO, a separate page for each roof type and area, a complete Google Business Profile and steady customer reviews. Every roofing website from ConstructionWebDesign.uk includes on-page local SEO, with page titles, headings and copy that name your services and the towns you cover. Nobody can honestly guarantee a ranking, but that structure gives Google clear signals about what you do and where.",
    },
    {
      q: "Which accreditations should a roofer show on their website?",
      a: "A roofer should show the accreditations and memberships they actually hold, such as NFRC (National Federation of Roofing Contractors), CompetentRoofer, which lets registered roofers self-certify re-roofing work under Building Regulations, TrustMark, and their public liability insurance. On a ConstructionWebDesign.uk roofing website, each badge sits near the top of the page with its registration number linked to the official register. Schemes you don't hold are left off.",
    },
    {
      q: "Should a roofing website have an emergency callout page?",
      a: "Yes, a roofing website should have an emergency callout page if you take urgent work such as storm damage, leaks or slipped tiles. Website design for roofers from ConstructionWebDesign.uk puts your phone number and a one-tap call button at the top of that page, kept visible on mobile, and explains which areas you cover and what you can do on the day, such as temporary covers.",
    },
    {
      q: "Can customers send photos of roof damage through the website?",
      a: "Yes, customers can send photos of roof damage through the quote form on a ConstructionWebDesign.uk roofing website. The form asks for a photo, a postcode and a short description of the problem, so you can see the slipped slates, cracked flashing or split flat roof before you call back and decide whether it needs a site visit.",
    },
  ],
  guides: [
    {
      href: "/blog/how-much-should-a-construction-website-cost",
      label: "How much does a construction website cost in the UK?",
    },
    {
      href: "/blog/how-to-get-more-construction-leads",
      label: "How to get more construction leads without paying",
    },
    {
      href: "/blog/local-seo-for-builders",
      label: "Local SEO for builders: rank for \"builder near me\"",
    },
    {
      href: "/blog/google-business-profile-for-builders",
      label: "Google Business Profile for builders: setup guide",
    },
  ],
  finalCta: {
    headline: "Talk to us about your roofing website.",
    highlight: "your roofing website",
    sub: "Book a free 30-minute call to talk through your roofing work and the areas you cover. Fixed £1,000 price, and your website goes live in 7 days.",
  },
};
